import { useState, useEffect } from 'react';
import { motion } from 'motion/react';

/**
 * Client-Only Motion Hook (2025 Standards)
 * 
 * Follows Next.js 15+ best practices by conditionally rendering motion components
 * only on the client to prevent all hydration mismatches.
 * 
 * @returns {object} - Motion components that are SSR-safe
 */
export function useClientMotion() {
  const [isClient, setIsClient] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    setIsClient(true);
    
    // Check motion preference after hydration
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      try {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        
        const handleChange = (e: MediaQueryListEvent) => {
          setPrefersReducedMotion(e.matches);
        };
        
        if (mediaQuery.addEventListener) {
          mediaQuery.addEventListener('change', handleChange);
          return () => mediaQuery.removeEventListener('change', handleChange);
        } else {
          // Legacy browser support for older MediaQueryList API
          mediaQuery.addListener(handleChange);
          return () => mediaQuery.removeListener(handleChange);
        }
      } catch (error) {
        console.warn('matchMedia not supported');
      }
    }
  }, []);

  // ✅ 2025 STANDARD: Conditional component rendering
  const MotionDiv = isClient && !prefersReducedMotion ? motion.div : 'div';
  const MotionHeader = isClient && !prefersReducedMotion ? motion.header : 'header';
  const MotionH1 = isClient && !prefersReducedMotion ? motion.h1 : 'h1';
  const MotionH2 = isClient && !prefersReducedMotion ? motion.h2 : 'h2';
  const MotionP = isClient && !prefersReducedMotion ? motion.p : 'p';
  const MotionSection = isClient && !prefersReducedMotion ? motion.section : 'section';
  
  return {
    isClient,
    prefersReducedMotion,
    shouldAnimate: isClient && !prefersReducedMotion,
    // SSR-safe motion components
    MotionDiv,
    MotionHeader, 
    MotionH1,
    MotionH2: isClient && !prefersReducedMotion ? motion.h2 : 'h2',
    MotionP,
    MotionSection,
    MotionImg: isClient && !prefersReducedMotion ? motion.img : 'img',
    // Conditional props helper
    motionProps: (props: any) => isClient && !prefersReducedMotion ? props : {}
  };
}

// Backward compatibility
export const useProgressiveMotion = useClientMotion;
export const useReducedMotionSSR = () => {
  const { shouldAnimate } = useClientMotion();
  return !shouldAnimate;
}; 